import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { api, saveAuth } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

const Field = ({ label, type = "text", value, onChange, testid, required = true, placeholder }) => (
  <div>
    <label className="eyebrow block mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-4 py-4 rounded-xl bg-[#121214] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition"
    />
  </div>
);

export function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const next = params.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      saveAuth(res.data.token, res.data.user);
      toast.success("Welcome back");
      const dest =
        res.data.user.role === "admin"
          ? "/admin"
          : res.data.user.role === "provider"
          ? "/provider-dashboard"
          : next;
      nav(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="login-page">
      <div className="eyebrow mb-4">Sign in</div>
      <h1 className="font-serif-display text-4xl text-white mb-10">Welcome back.</h1>
      <form onSubmit={submit} className="space-y-6">
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email-input" />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password-input" />
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} weight="bold" />
        </button>
      </form>
      <p className="mt-8 text-sm text-zinc-400">
        No account? <Link to="/signup" className="text-[#D4AF37] hover:underline" data-testid="signup-link">Create one</Link>
      </p>
    </div>
  );
}

export function Signup() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("customer");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/signup", {
        email, password, full_name: fullName, phone, role
      });
      saveAuth(res.data.token, res.data.user);
      toast.success("Account created");
      nav(role === "provider" ? "/become-provider" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="signup-page">
      <div className="eyebrow mb-4">Create account</div>
      <h1 className="font-serif-display text-4xl text-white mb-10">Join RideXelite.</h1>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Full name" value={fullName} onChange={setFullName} testid="signup-name-input" />
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="signup-email-input" />
        <Field label="Phone" value={phone} onChange={setPhone} testid="signup-phone-input" required={false} />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="signup-password-input" />
        <div>
          <div className="eyebrow mb-3">I want to</div>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setRole("customer")}
              data-testid="role-customer-btn"
              className={`px-4 py-4 rounded-xl border text-sm transition ${role === "customer" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
            >
              Book services
            </button>
            <button
              type="button"
              onClick={() => setRole("provider")}
              data-testid="role-provider-btn"
              className={`px-4 py-4 rounded-xl border text-sm transition ${role === "provider" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
            >
              Offer services
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="signup-submit-btn"
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-60"
        >
          {loading ? "Creating…" : "Create account"} <ArrowRight size={16} weight="bold" />
        </button>
      </form>
      <p className="mt-8 text-sm text-zinc-400">
        Already have an account? <Link to="/login" className="text-[#D4AF37] hover:underline">Sign in</Link>
      </p>
    </div>
  );
}
